/**
 * Web push: notifications (a job offer, the driver on the way, ...) even with the app
 * closed. The browser subscribes with the firm's VAPID key (from GET /firm) and the
 * subscription is registered with the API as a "web" device of the signed-in customer or
 * driver. On iPhone and iPad, web push only works once the app has been added to the
 * Home Screen.
 */
import { useCallback, useEffect, useState } from "react";
import type { WebApp } from "../app";
import { FriendlyError, post } from "./api";

/** Where each app registers its devices. */
const devicesPath = (app: WebApp) => `/${app}/devices`;

export type PushSupport = "supported" | "add-to-home-screen" | "unsupported";
export type PushState = "on" | "off" | "blocked";

const isIos = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as { standalone?: boolean }).standalone === true;

export function pushSupport(): PushSupport {
  const capable =
    "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  if (capable) return "supported";
  // Safari on iPhone has web push only for apps on the Home Screen.
  if (isIos() && !isStandalone()) return "add-to-home-screen";
  return "unsupported";
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

export async function pushState(): Promise<PushState> {
  if (pushSupport() !== "supported") return "off";
  if (Notification.permission === "denied") return "blocked";
  if (Notification.permission !== "granted") return "off";
  const subscription = await (await registration())?.pushManager.getSubscription();
  return subscription ? "on" : "off";
}

function keyBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

/** Subscribing asks the browser's push service, which can hang when it can't be reached. */
const SUBSCRIBE_TIMEOUT_MS = 20_000;
const TIMED_OUT = new Error("Timed out");

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(TIMED_OUT), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/** Asks permission (if needed), subscribes and tells the API. Call from a tap. */
export async function enablePush(app: WebApp, vapidPublicKey: string): Promise<PushState> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "blocked" : "off";
  const reg = (await registration()) ?? (await navigator.serviceWorker.ready);
  let subscription = await reg.pushManager.getSubscription();
  // A subscription made with another key (the firm changed keys) can't be used.
  const current = subscription?.options.applicationServerKey;
  if (subscription && current) {
    const want = keyBytes(vapidPublicKey);
    const have = new Uint8Array(current);
    if (have.length !== want.length || have.some((b, i) => b !== want[i])) {
      await subscription.unsubscribe();
      subscription = null;
    }
  }
  try {
    subscription ??= await withTimeout(
      reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(vapidPublicKey),
      }),
      SUBSCRIBE_TIMEOUT_MS,
    );
  } catch (error) {
    throw new FriendlyError(
      error === TIMED_OUT
        ? "Notifications couldn't be turned on just now. Check your connection and try again."
        : // e.g. a private window, where browsers don't allow them.
          "This browser couldn't turn on notifications (private windows can't have them). We'll still text you about your trips.",
    );
  }
  await post(devicesPath(app), { platform: "web", token: JSON.stringify(subscription) });
  return "on";
}

/** Stops notifications to this browser. The API forgets it the next time it tries one. */
export async function disablePush(): Promise<void> {
  const subscription = await (await registration())?.pushManager.getSubscription();
  await subscription?.unsubscribe();
}

/** Re-registers an existing subscription, e.g. after signing in on this browser again. */
export async function refreshPushRegistration(app: WebApp): Promise<void> {
  if ((await pushState()) !== "on") return;
  const subscription = await (await registration())?.pushManager.getSubscription();
  if (subscription) {
    await post(devicesPath(app), { platform: "web", token: JSON.stringify(subscription) });
  }
}

/** The service worker shows notifications and lets the app open without a connection. */
export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((error) => {
      console.warn("Service worker not registered", error);
    });
  });
}

/**
 * A notifications switch: whether they're on in this browser, and turning them on or off.
 * `available` is false when the firm hasn't set up web push or the browser can't do it.
 */
export function usePush(app: WebApp, vapidPublicKey: string | null) {
  const [state, setState] = useState<PushState | "busy">("off");
  const support = pushSupport();
  useEffect(() => {
    void pushState().then(setState);
  }, []);
  const toggle = useCallback(async (): Promise<PushState> => {
    if (!vapidPublicKey) return "off";
    const was = state === "busy" ? "off" : state;
    setState("busy");
    try {
      let next: PushState = "off";
      if (was === "on") await disablePush();
      else next = await enablePush(app, vapidPublicKey);
      setState(next);
      return next;
    } catch (error) {
      setState(was);
      throw error;
    }
  }, [app, vapidPublicKey, state]);
  return {
    state,
    support,
    available: Boolean(vapidPublicKey) && support !== "unsupported",
    toggle,
  };
}
